import React, { memo, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import { PiSmileySad } from 'react-icons/pi';

const formatAmount = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const renderAmount = (value) => {
  const formatted = formatAmount(value);
  if (!formatted) {
    return <span className={styles.pendingValue}>—</span>;
  }
  return formatted;
};

const varianceWord = (value) => {
  const num = Number(value);
  if (Math.abs(num) < 0.01) return { word: 'Balanced', className: styles.varianceBalanced };
  if (num < 0) return { word: 'Short', className: styles.varianceShort };
  return { word: 'Over', className: styles.varianceOver };
};

const renderVariance = (value) => {
  if (value === null || value === undefined) {
    return <span className={styles.pendingValue}>—</span>;
  }
  const formatted = formatAmount(value);
  if (!formatted) return <span className={styles.pendingValue}>—</span>;
  const tone = varianceWord(value);
  return (
    <span className={tone.className}>
      {tone.word} {formatted}
    </span>
  );
};

const formatSealedAt = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    timeZone: 'Asia/Colombo',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BusinessDayTable = memo(
  ({
    data = [],
    isLoading = false,
    error = null,
    pagination = {},
    setSearchParams,
    page = 1,
    limit = 10,
  }) => {
    const transformedData = useMemo(
      () =>
        (data || []).map((item) => ({
          id: item.participationId,
          businessDayLabel: item.businessDayLabel || '—',
          registerLabel: item.registerLabel || item.cashierPoint?.label || '—',
          status: item.status || '—',
          businessDayStatus: item.businessDayStatus || '—',
          expectedCash: renderAmount(item.expectedCash),
          countedCash: renderAmount(item.countedCash),
          variance: renderVariance(item.variance),
          deposit: renderAmount(item.deposit),
          floatAmount: renderAmount(item.float),
          varianceReason: item.varianceReason || '—',
          sealedAt: formatSealedAt(item.sealedAt),
          unverified: item.unverified ? 'Unverified' : '—',
        })),
      [data]
    );

    const handleChangePage = useCallback(
      (event, newPage) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(newPage + 1));
          return next;
        });
      },
      [setSearchParams]
    );

    const handleChangeRowsPerPage = useCallback(
      (event) => {
        const newLimit = parseInt(event?.target?.value, 10) || 10;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(newLimit));
          next.set('page', '1');
          return next;
        });
      },
      [setSearchParams]
    );

    const columns = useMemo(
      () => [
        { id: 'businessDayLabel', label: 'Business Day', minWidth: 200 },
        { id: 'registerLabel', label: 'Register', minWidth: 140 },
        { id: 'status', label: 'Status', minWidth: 140 },
        { id: 'businessDayStatus', label: 'Day Status', minWidth: 110 },
        { id: 'expectedCash', label: 'Expected', minWidth: 120 },
        { id: 'countedCash', label: 'Counted', minWidth: 120 },
        { id: 'variance', label: 'Variance', minWidth: 150 },
        { id: 'deposit', label: 'Deposit', minWidth: 120 },
        { id: 'floatAmount', label: 'Float', minWidth: 120 },
        { id: 'varianceReason', label: 'Variance Reason', minWidth: 160 },
        { id: 'sealedAt', label: 'Sealed At', minWidth: 170 },
        { id: 'unverified', label: 'Unverified', minWidth: 120 },
      ],
      []
    );

    const totalCount = pagination?.totalRecords ?? 0;

    useEffect(() => {
      if (isLoading || error || transformedData.length || totalCount <= 0 || page <= 1) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('page') === '1') return prev;
        next.set('page', '1');
        return next;
      });
    }, [isLoading, error, transformedData.length, totalCount, page, setSearchParams]);

    if (isLoading) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>Unable to load Business Day</p>
            <p className={styles.emptyStateDesc}>{error}</p>
          </div>
        </div>
      );
    }

    if (!transformedData.length) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>No register participation found</p>
            <p className={styles.emptyStateDesc}>
              Try a wider date span or another Business Day.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Table
          columns={columns}
          rows={transformedData}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={totalCount}
          disableInternalPagination
        />
      </div>
    );
  }
);

BusinessDayTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  pagination: PropTypes.object,
  setSearchParams: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
};

export default BusinessDayTable;
